import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { patchRoverTdm, signRoverTdmFile, uploadRoverTdmAsset } from '@/api/Rover/applicationTDM';
import type { RoverSignAssetPayload, RoverTdmAssetPayloadType } from '@/api/Rover/applicationTDM/types';
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookFileInput } from '@/components/UI/HookForm/Form/File';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import Modal from '@/components/UI/Modal';
import { RoverReactQueryKey } from '@/constants/ReactQueryKeys';
import { type UploadFnReturn, useFileUpload } from '@/hooks/useFileUpload';
import { notifyUser } from '@/store/Toast';
import type { TdmType } from '@/types/Tdm';
import { capitalize, getAcceptedFileTypes, sanitizeResponseError } from '@/utils';

interface SignedData {
  url: string;
  fKey: string;
}

interface IUploadFormProps {
  tdmAssetType: 'template' | 'document';
  tdm: any;
  tdmType: TdmType;
  handleModal: (tdmAssetType: 'template' | 'document') => void;
}

const roverTdmQueryKeyMapping = {
  task: RoverReactQueryKey.GET_ROVER_TASKS,
  deliverable: RoverReactQueryKey.GET_ROVER_DELIVERABLES,
  milestone: RoverReactQueryKey.GET_ROVER_MILESTONES,
};

const roverTdmAssetsQueryKeyMapping = {
  task: RoverReactQueryKey.GET_ROVER_TASK_ASSETS,
  deliverable: RoverReactQueryKey.GET_ROVER_DELIVERABLE_ASSETS,
  milestone: RoverReactQueryKey.GET_ROVER_MILESTONE_ASSETS,
};

const UploadForm = ({ tdmAssetType, tdm, tdmType, handleModal }: IUploadFormProps) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { upload } = useFileUpload({ signAsset: signRoverTdmFile });

  const changeTdmStatus = useMutation(
    () =>
      patchRoverTdm({
        tdmType,
        tdmId: tdm.id,
        data: {
          status: 'UNDER_REVIEW',
        },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([roverTdmQueryKeyMapping[tdmType]]);
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const uploadRoverTdm = useMutation(
    (files: { assets: RoverTdmAssetPayloadType[] }) => uploadRoverTdmAsset({ tdmType, tdmId: tdm.id, files }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([roverTdmAssetsQueryKeyMapping[tdmType]]);
        handleModal(tdmAssetType);
        if ((tdmType === 'deliverable' || tdmType === 'milestone') && tdmAssetType === 'document') {
          changeTdmStatus.mutate();
        }
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const fileUploadBulk = useMutation((payload: { signAssetPayload: RoverSignAssetPayload[]; files: File[] }) => upload(payload), {
    onSuccess: ({ sign, files }: UploadFnReturn<File, SignedData>) => {
      let signRes: SignedData[] = [];
      if (Array.isArray(sign)) {
        signRes = [...sign];
      } else {
        signRes = [{ ...sign }];
      }
      const payload = signRes.map((sign, index) => ({
        name: files[index].name,
        key: sign.fKey,
        contentType: files[index].type,
        size: files[index].size,
        assetType: tdmAssetType,
      }));
      uploadRoverTdm.mutate({ assets: payload });
    },
    onError: (error: any) => {
      const resError = sanitizeResponseError(error);
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: resError,
          toastType: 'failed',
        })
      );
    },
  });

  const handleSubmit = (selectedFiles: { [key: string]: File[] }) => {
    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    selectedFiles[tdmAssetType].forEach((file: File) => {
      const acceptedFileTypes = getAcceptedFileTypes().split(',');
      if (acceptedFileTypes.includes(file.type)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    });

    if (invalidFiles.length > 0) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: `${invalidFiles.join(', ')} file types are not accepted to upload.`,
          toastType: 'failed',
        })
      );
    }

    if (validFiles.length > 0) {
      const signPayload = validFiles.map<RoverSignAssetPayload>((file) => ({
        key: file.name,
        type: file.type,
        resource: 'tdmRover',
        resourceId: tdm.id,
        path: 'files',
      }));

      fileUploadBulk.mutate({
        signAssetPayload: signPayload,
        files: validFiles,
      });
    }
  };

  return (
    <Modal>
      <HookFormProvider>
        <HookForm onSubmit={handleSubmit}>
          <h2 className='capitalize mt-0'>{tdmAssetType}s Upload</h2>
          <HookFileInput
            label={capitalize(tdmAssetType)}
            name={tdmAssetType}
            id={tdmAssetType}
            disabled={fileUploadBulk.isLoading || uploadRoverTdm.isLoading}
            multiple
          />
          <div className='flex items-end justify-end gap-2 mt-6'>
            <Button
              label={'Cancel'}
              disabled={fileUploadBulk.isLoading || uploadRoverTdm.isLoading}
              btnStyle='btn-neutral'
              btnType='button'
              onClick={handleModal}
            />
            <Button
              label={fileUploadBulk.isLoading ? 'Uploading' : uploadRoverTdm.isLoading ? 'Saving' : 'Upload'}
              btnStyle='btn-primary'
              disabled={fileUploadBulk.isLoading || uploadRoverTdm.isLoading}
              btnType='submit'
            />
          </div>
        </HookForm>
      </HookFormProvider>
    </Modal>
  );
};

export default UploadForm;
